import { checkEmployeeHierarchy } from "@/api/utils";
import { OpenFolderButton } from "@/components/Buttons";
import { ProjectProps } from "@/interfaces/project_types";
import { useState, useMemo } from "react";
import { SubmittalOverview } from "./UI/SubmittalOverview";
import { TaskOverview } from "./UI/TaskOverview";
import { Description } from "./UI/Description";
import { calculateCategoryCounts } from "./Utils/calculateCategoryCounts";
import { useAuth } from "@/App";
import { SimilarProjects } from "./UI/SimilarProjects";
import { DownloadDocumentsButton } from "./UI/DownloadDocumentsButton";
import { CreateRFIButton, UploadFileButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { CreateSubmittalButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { AssignTasksButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { UpdateProjectButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { BaseExpandableRow } from "@/components/_Shared/Table/UI/ExpandableRow/ExpandableRow";
import DeleteProjectButton from "./UI/DeleteProjectButton";

export type CategoryCounts = {
    Mechanical: { total: number; completed: number; };
    Electrical: { total: number; completed: number; };
    Plumbing: { total: number; completed: number; };
    "Fire Protection": { total: number; completed: number; };
    Other: { total: number; completed: number; };
}

/**
 * A Component that shows when the user clicks on a row on the table.
 * This is where the description of the project will be stored
 * 
 * TODO: Refactor this so that all the calculations are done in the backend
 *
 */
export const ProjectRow = ({ data }: { data: ProjectProps }) => {
    const { user } = useAuth()
    const role = user?.role ?? ""
    
    // I might refactor this into a hook later
    // Or toss them into their own respective components
    const submittals = useMemo(() => data.submittals ?? [], [data.submittals])
    const tasks = useMemo(() => data.tasks ?? [], [data.tasks])

    const [submittalCounts, setSubmittalCounts] = useState<CategoryCounts>({
        Mechanical: { total: 0, completed: 0 },
        Electrical: { total: 0, completed: 0 },
        Plumbing: { total: 0, completed: 0 },
        "Fire Protection": { total: 0, completed: 0 },
        Other: { total: 0, completed: 0 }
    })

    const [taskCounts, setTaskCounts] = useState<CategoryCounts>({
        Mechanical: { total: 0, completed: 0 },
        Electrical: { total: 0, completed: 0 },
        Plumbing: { total: 0, completed: 0 },
        "Fire Protection": { total: 0, completed: 0 },
        Other: { total: 0, completed: 0 }
    })

    useMemo(() => {
        setTaskCounts(calculateCategoryCounts(tasks))
        setSubmittalCounts(calculateCategoryCounts(submittals))
    }, [tasks, submittals]);

    const totalTasks = Object.values(taskCounts).reduce((sum, { total }) => sum + total, 0);
    const totalCompletedTasks = Object.values(taskCounts).reduce((sum, { completed }) => sum + completed, 0);  
    const taskTotalProgress = totalTasks > 0 ? (totalCompletedTasks / totalTasks) * 100 : 0;

    /*
    const handleDelete = async () => {
        if (confirm("Are you sure you want to delete this project?") && confirm("Are you really sure?")) {
            try {
                if (!data.project_id) {
                    throw new Error("Project ID is undefined")
                }

                const response = await deleteProject(data.project_id)
                if (response === 204) {
                    alert("Project deleted successfully")
                    window.location.href = "/projects/"
                } else {
                    alert("Error deleting project:" + response)
                }
            } catch (error: unknown) {
                if (error instanceof AxiosError) {
                    if (error.response?.status === 400) {
                        // 400 Bad Request
                        return 400
                    } else if (error.response?.status === 403) {
                        // 403 Forbidden
                        return 403
                    }
                }
                alert("Error deleting project: Error" + error)
            }
        }
    }
    */

    const totalSubmittals = Object.values(submittalCounts).reduce((sum, { total }) => sum + total, 0);
    const totalCompletedSubmittals = Object.values(submittalCounts).reduce((sum, { completed }) => sum + completed, 0);
    const submittalTotalProgress = totalSubmittals > 0 ? (totalCompletedSubmittals / totalSubmittals) * 100 : 0;
    
    return (
        <BaseExpandableRow>
            <div className="flex flex-row gap-5 m-5">
                {(checkEmployeeHierarchy(role) === "ADMIN" || checkEmployeeHierarchy(role) === "MANAGERS") &&
                    <>
                        <UpdateProjectButton project_id={data.project_id ?? ""} />
                        <DeleteProjectButton project_id={data.project_id ?? ""} />
                        <AssignTasksButton project_id={data.project_id ?? ""} />
                        <CreateSubmittalButton project_id={data.project_id ?? ""} />
                        <CreateRFIButton project_id={data.project_id ?? ""} />
                        <UploadFileButton project_id={data.project_id ?? ""} folder_location={data.folder_location ?? ""} />
                        <DownloadDocumentsButton project_id={data.project_id ?? ""} />
                    </>}


                {/* <OpenFolderButton folder_path={'projects\\' + data.folder_location} /> For the cloud*/}
                <OpenFolderButton folder_path={data.folder_location ?? ""} />
            </div>

            <div className="flex flex-col gap-5 p-5">
                <Description description={data.description ?? ""} />

                <TaskOverview tasks={tasks} taskCounts={taskCounts} totalTasks={totalTasks} totalProgress={taskTotalProgress} />

                <SubmittalOverview submittals={submittals} submittalCounts={submittalCounts} totalSubmittals={totalSubmittals} totalProgress={submittalTotalProgress} />
            </div>

            <div className="flex flex-col gap-5 p-5">
                <SimilarProjects project={data} />
            </div>

        </BaseExpandableRow>
    );
};

// This is a smaller version of the project row, where it only shows the description and buttons
export const MiniProjectRow = ({ data }: { data: ProjectProps }) => {
    const { user } = useAuth()
    const role = user?.role ?? ""

    return (
        <BaseExpandableRow>
            <div className="flex flex-row gap-5 m-5">
                {(checkEmployeeHierarchy(role) === "ADMIN" || checkEmployeeHierarchy(role) === "MANAGERS") &&
                    <>
                        <UpdateProjectButton project_id={data.project_id ?? ""} />
                        <AssignTasksButton project_id={data.project_id ?? ""} />
                        <CreateSubmittalButton project_id={data.project_id ?? ""} />
                        <CreateRFIButton project_id={data.project_id ?? ""} />
                        <UploadFileButton project_id={data.project_id ?? ""} folder_location={data.folder_location ?? ""} />
                        <DownloadDocumentsButton project_id={data.project_id ?? ""} />
                    </>}


                {/* <OpenFolderButton folder_path={'projects\\' + data.folder_location} /> For the cloud*/}
                <OpenFolderButton folder_path={data.folder_location ?? ""} />
            </div>

            <div className="flex flex-col gap-5 p-5">
                <Description description={data.description ?? ""} />
            </div>

        </BaseExpandableRow>
    )
}